import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useStrava } from '../contexts/StravaContext';
import ActivityCard from './ActivityCard';
import TempoVisualization from './TempoVisualization';

const { FiUser, FiActivity, FiLogOut, FiRefreshCw, FiTrendingUp, FiArrowRight, FiPalette } = FiIcons;

const Dashboard = () => {
  const navigate = useNavigate();
  const { athlete, activities, logout, loading } = useStrava();
  const [selectedActivity, setSelectedActivity] = useState(null);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const formatDistance = (meters) => {
    return (meters / 1000).toFixed(2) + ' km';
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const formatPace = (metersPerSecond) => {
    const minutesPerKm = 1000 / (metersPerSecond * 60);
    const minutes = Math.floor(minutesPerKm);
    const seconds = Math.floor((minutesPerKm - minutes) * 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}/km`;
  };

  const handleViewActivity = (activity) => {
    navigate(`/activity/${activity.id}`);
  };

  const handleViewAnalysis = (activity) => {
    navigate(`/analysis`);
  };

  return (
    <div className="min-h-screen p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div 
          className="flex items-center justify-between mb-8"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-pink-500 rounded-xl flex items-center justify-center">
              <SafeIcon icon={FiActivity} className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">TempoPainter</h1>
              <p className="text-gray-400">Welcome back, {athlete?.firstname}!</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            {athlete?.profile_medium && (
              <img 
                src={athlete.profile_medium} 
                alt="Profile" 
                className="w-10 h-10 rounded-full border-2 border-white/20"
              />
            )}
            <motion.button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-xl transition-all duration-300"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <SafeIcon icon={FiLogOut} className="w-4 h-4" />
              Logout
            </motion.button>
          </div>
        </motion.div>

        {/* Main Content */}
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Activities List */}
          <motion.div
            className="space-y-4"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                <SafeIcon icon={FiTrendingUp} className="w-5 h-5" />
                Recent Activities
              </h2>
              
              {loading && (
                <SafeIcon icon={FiRefreshCw} className="w-5 h-5 text-gray-400 animate-spin" />
              )}
            </div>
            
            <div className="space-y-3">
              {activities.map((activity, index) => (
                <motion.div
                  key={activity.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <ActivityCard
                    activity={activity}
                    isSelected={selectedActivity?.id === activity.id}
                    onClick={() => setSelectedActivity(activity)}
                    formatDistance={formatDistance}
                    formatTime={formatTime}
                    formatPace={formatPace}
                  />
                </motion.div>
              ))}
            </div>
            
            {activities.length === 0 && !loading && (
              <div className="text-center py-12 text-gray-400">
                <SafeIcon icon={FiActivity} className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No activities found. Start running and sync with Strava!</p>
              </div>
            )}
          </motion.div>

          {/* Visualization Panel */}
          <motion.div
            className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-white">Tempo Visualization</h2>
              
              {selectedActivity && (
                <motion.button
                  onClick={() => handleViewActivity(selectedActivity)}
                  className="flex items-center gap-1 text-orange-400 hover:text-orange-300 text-sm font-medium"
                  whileHover={{ x: 3 }}
                >
                  View Full Analysis
                  <SafeIcon icon={FiArrowRight} className="w-4 h-4" />
                </motion.button>
              )}
            </div>
            
            <AnimatePresence mode="wait">
              {selectedActivity ? (
                <motion.div
                  key={selectedActivity.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <TempoVisualization
                    activity={selectedActivity}
                    formatPace={formatPace}
                  />
                  
                  <motion.div
                    className="mt-6 p-4 bg-gradient-to-r from-orange-500/20 to-pink-500/20 border border-orange-500/30 rounded-xl"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                  >
                    <div className="flex flex-wrap items-center justify-between gap-4">
                      <div>
                        <h3 className="text-white font-medium">AI Artwork Generation</h3>
                        <p className="text-gray-400 text-sm">Create a unique artistic interpretation of this activity</p>
                      </div>
                      <div className="flex gap-2">
                        <motion.button
                          onClick={() => handleViewActivity(selectedActivity)}
                          className="flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors"
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          Generate Art
                        </motion.button>
                        <motion.button
                          onClick={() => handleViewAnalysis(selectedActivity)}
                          className="flex items-center gap-2 px-4 py-2 bg-purple-500/30 hover:bg-purple-500/40 text-purple-300 rounded-lg transition-colors"
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          <SafeIcon icon={FiPalette} className="w-4 h-4" />
                          Analysis
                        </motion.button>
                      </div>
                    </div>
                  </motion.div>
                </motion.div>
              ) : (
                <motion.div
                  className="flex items-center justify-center h-96 text-gray-400"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <div className="text-center">
                    <SafeIcon icon={FiActivity} className="w-16 h-16 mx-auto mb-4 opacity-30" />
                    <p>Select an activity to view its tempo visualization</p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;